/**
 * The manual, in the app.
 *
 * One source for both: this page is the FAQ staff read on screen and,
 * printed, the booklet handed to a site. A separate document would
 * drift from the product within a month, and the printed one is
 * always the copy nobody updates.
 */
export interface HelpSection {
  readonly id: string;
  readonly title: string;
  readonly audience: 'site' | 'office' | 'both';
  readonly items: ReadonlyArray<{ q: string; a: string }>;
}

export const HELP: readonly HelpSection[] = [
  {
    id: 'start',
    title: 'Getting started',
    audience: 'both',
    items: [
      {
        q: 'What is this for?',
        a: 'Head office sends notices to sites and can see who read '
          + 'them. Sites report faults and requests back, and can see '
          + 'what is happening with each one. It replaces group chats '
          + 'where important messages scroll away.',
      },
      {
        q: 'What is a node?',
        a: 'Anything that signs in: a counter PC, a phone, or a person '
          + 'at head office. Your plan is counted in nodes. Staff PINs '
          + 'are not nodes and are unlimited.',
      },
    ],
  },
  {
    id: 'board',
    title: 'The board',
    audience: 'site',
    items: [
      {
        q: 'What is pinned at the top?',
        a: 'Notices head office wants kept in view. They stay until '
          + 'they expire, and they appear whichever category you are '
          + 'looking at.',
      },
      {
        q: 'Why does a message ask me to sign for it?',
        a: 'Some notices need a named person to confirm they were '
          + 'read. Enter your four digit staff PIN. The receipt then '
          + 'shows your name, the screen and the time.',
      },
      {
        q: 'What do the coloured chips do?',
        a: 'They filter the board by category. The number on a chip '
          + 'is how many messages there have not been opened.',
      },
    ],
  },
  {
    id: 'tickets',
    title: 'Tickets',
    audience: 'both',
    items: [
      {
        q: 'How do I report a fault?',
        a: 'Report something on the Tickets screen. One line for what '
          + 'is wrong, then any detail that helps. Pick the category '
          + 'so it reaches the right team, and tick Urgent only when '
          + 'it genuinely cannot wait.',
      },
      {
        q: 'How do I add a photo?',
        a: 'Attach a photo or PDF before sending. On an existing '
          + 'ticket, the camera icon shows a QR code: scan it with any '
          + 'phone and the photo uploads to that ticket. No app or '
          + 'login needed on the phone.',
      },
      {
        q: 'Who is dealing with it?',
        a: 'The owner is shown once somebody picks it up. Everyone in '
          + 'that team sees the whole thread, and any of them can '
          + 'reply or close it.',
      },
    ],
  },
  {
    id: 'chat',
    title: 'Chat',
    audience: 'both',
    items: [
      {
        q: 'How is chat different from a ticket?',
        a: 'Chat is a conversation. A ticket is a job with a status '
          + 'that someone has to finish. Ask a question in chat; '
          + 'report a fault as a ticket.',
      },
      {
        q: 'Why did a conversation close on its own?',
        a: 'Conversations nobody answered close at the end of the '
          + 'day. They stay in history as a record that head office '
          + 'asked and the site did not reply.',
      },
    ],
  },
  {
    id: 'admin',
    title: 'Running the account',
    audience: 'office',
    items: [
      {
        q: 'How do I add a counter PC?',
        a: 'Settings, Sites and nodes. Download the Windows app from '
          + 'that page, install it on the machine, then issue an '
          + 'enrolment code and enter it. The code is single use and '
          + 'expires.',
      },
      {
        q: 'How do I add a person?',
        a: 'Settings, People. Tick the sites and categories they '
          + 'cover, or tick Full access for an owner. They set their '
          + 'own password from the invite link, so nobody else ever '
          + 'knows it.',
      },
      {
        q: 'Somebody sees nothing. Why?',
        a: 'A supervisor needs both a site and a category. With one '
          + 'and not the other they see nothing and get no alerts. '
          + 'The People list marks this in amber.',
      },
      {
        q: 'Someone left. What do I do?',
        a: 'Sign out everywhere on their row ends every session and '
          + 'trusted device. Deactivating keeps their name on past '
          + 'acknowledgements, which is why nothing is deleted.',
      },
      {
        q: 'How do I read the reports?',
        a: 'Read coverage shows who opened a notice and, more '
          + 'usefully, who did not. Needs attention lists anything '
          + 'past its response time.',
      },
    ],
  },
];
